// ----------------------------------------------------------------------------
// Electribe Export Module
// -------------------------------------------------------------------------
// Builds valid Korg Electribe Sampler `.esx` (ESX) and `.es1` (ES-1) files
// from PCM sample data, matching the binary format the hardware expects.
//
// ESX: raw 16-bit Big-Endian PCM, variable sample rate, 256 mono / 128 stereo.
// ES1: proprietary ADPCM compression, fixed 32 kHz, 100 mono / 50 stereo.
// ----------------------------------------------------------------------------

// ---- ESX constants ---------------------------------------------------------
const ESX_FILE_HEADER_OFF   = 0x00000000;
const ESX_SAMPLE_SECTION    = 0x001B0000;
const ESX_NUM_MONO_OFF      = 0x001B0020;
const ESX_NUM_STEREO_OFF    = 0x001B0024;
const ESX_CUR_OFFSET_OFF    = 0x001B0028;
const ESX_MONO_HEADER_OFF   = 0x001B0100;
const ESX_STEREO_HEADER_OFF = 0x001B2900;
const ESX_SAMPLE_DATA_OFF   = 0x00250000;
const ESX_MONO_HEADER_SIZE  = 40;
const ESX_STEREO_HEADER_SIZE = 44;
const ESX_MAX_MONO          = 256;
const ESX_MAX_STEREO        = 128;
const ESX_SAMPLE_BLOCK_HEADER = 16;

// ---- ES1 constants ---------------------------------------------------------
// File layout (verified against real Korg .es1 files):
//   0x000000   16       HDR1 ("KORG" + version + "W" + 02 + checksum)
//   0x000100   ~0x7FF00 pattern/global data (BPM, parts, steps, effects)
//   0x080000   16       HDR2 ("KORG" + version + "W" + 01 + checksum)
//   0x080014   2600     100 mono sample headers × 26 bytes
//   0x080A0C   1400     50 stereo sample headers × 28 bytes
//   0x0A0000   ~3MB     sample data (ADPCM compressed, 32 bytes per 32-sample frame)
//   File size: 3,801,088 bytes (0x3A0000 = 29 × 128KB)
//
// MONO slot record (26 bytes), verified against real Korg files:
//   [0-2]   STADDR_A — Range A start (24-bit BE, = file_offset + 0x160000)
//   [3-5]   ENDADDR_A — Range A end (inclusive, = STADDR_A + nSamples - 1)
//   [6-8]   STADDR_B — Range B start (24-bit BE, = file_offset + 0x060000)
//   [9-11]  ENDADDR_B — Range B end (inclusive, 32-byte aligned)
//   [12-20] reserved / length info
//   [21]    STATUS — slot number for occupied; 0xFF for empty
//   [22-25] reserved
//
// STEREO slot record (28 bytes):
//   [0-2]   reserved
//   [3-5]   ENDADDR_A — end (sample end point)
//   [6-8]   STADDR_B — startAddr
//   [9-14]  reserved
//   [15]    reserved
//   [16-18] START — sample start point
//   [19-20] reserved
//   [21]    STATUS
//   [22-24] reserved
//   [25-27] ENDADDR_B — endAddr
const ES1_FILE_SIZE        = 3801088;
const ES1_HEADER_POS       = 0x80000;
const ES1_ADDR_OFFSET      = 0x60000;
const ES1_RANGE_A_OFFSET   = 0x160000;
const ES1_RANGE_B_OFFSET   = 0x60000;
const ES1_MONO_HEADER_OFF  = ES1_HEADER_POS + 20;
const ES1_MONO_HEADER_SIZE = 26;
const ES1_STEREO_HEADER_OFF = ES1_MONO_HEADER_OFF + 100 * ES1_MONO_HEADER_SIZE;
const ES1_STEREO_HEADER_SIZE = 28;
const ES1_NUM_MONO         = 100;
const ES1_NUM_STEREO       = 50;
const ES1_SAMPLE_RATE      = 32000;
const ES1_FRAME_SIZE       = 32;
const ES1_SAMPLE_DATA_OFF  = 0xA0000;
const ES1_AUDIO_ZONE_END   = 0x3A0000;

const ES1_INDEX_TABLE = [
   -1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
   -1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
   1,1,1,1,2,2,3,3,4,4,5,5,6,6,7,7,
   8,8,9,9,10,10,11,12,13,13,14,15,16,17,18,19
];

const ES1_STEPSIZE_TABLE = [
   [2,3,3,3,3,4,4,4,5,5,6,6,7,7,8,9,10,11,12,13,14,15,17,18,20,22,24,27,29,32,35,39,
    43,47,52,57,62,69,75,83,91,100,110,121,133,146,161,177,195,214,235,259,285,313,344,379,417,458,504,554,610,671,738,811],
   [29,32,34,37,39,42,45,49,52,56,60,65,70,75,80,86,93,100,107,115,124,133,143,154,166,178,191,206,221,238,255,274,
    295,317,341,367,394,424,455,490,526,566,608,654,703,756,813,874,939,1010,1086,1167,1255,1349,1450,1559,1677,1802,1938,2083,2240,2408,2589,2783],
   [442,465,488,512,538,565,593,622,653,686,720,756,794,834,875,919,965,1013,1064,1117,1173,1232,1293,1358,1426,1497,1572,1650,1733,1819,1910,2005,
    2106,2211,2321,2437,2559,2687,2821,2962,3110,3266,3429,3600,3780,3969,4168,4376,4595,4824,5065,5319,5584,5864,6157,6464,6787,7127,7483,7857,8250,8662,9095,9549],
   [6916,7089,7267,7448,7634,7825,8021,8221,8427,8638,8853,9075,9302,9534,9773,10017,10267,10524,10787,11057,11333,11616,11907,12204,12509,12822,13143,13471,13808,14153,14507,14870,
    15241,15622,16013,16413,16823,17244,17675,18117,18570,19034,19510,19998,20497,21010,21535,22073,22625,23191,23771,24365,24974,25598,26238,26894,27566,28256,28962,29686,30428,31189,31968,32767]
];

// ---- Helpers ---------------------------------------------------------------
function writeAscii(view, off, str, len, padByte) {
   const bytes = new Uint8Array(view.buffer);
   const pad = padByte === undefined ? 0x20 : padByte;
   for (let i = 0; i < len; i++) {
      bytes[off + i] = i < str.length ? str.charCodeAt(i) : pad;
   }
}

function writeUint32BE(view, off, val) {
   view.setUint32(off, val >>> 0, false);
}

function writeInt16BE(view, off, val) {
   view.setInt16(off, val, false);
}

function writeUint24BE(bytes, off, val) {
   bytes[off]     = (val >> 16) & 0xFF;
   bytes[off + 1] = (val >> 8) & 0xFF;
   bytes[off + 2] = val & 0xFF;
}

// Decode WAV ArrayBuffer into Int16Array PCM samples (mono or interleaved stereo)
export function decodeWavToPcm(wavBuffer) {
   const buf = wavBuffer instanceof ArrayBuffer ? wavBuffer
      : (wavBuffer.buffer ? wavBuffer.buffer.slice(wavBuffer.byteOffset || 0, (wavBuffer.byteOffset || 0) + wavBuffer.byteLength) : wavBuffer);
   const view = new DataView(buf);
   const bytes = new Uint8Array(buf);

   // Check RIFF header
   const magic = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]);
   if (magic !== 'RIFF') throw new Error('Not a WAV file');

   let pos = 12; // skip RIFF + size + WAVE
   let fmt = null, dataOff = 0, dataLen = 0;

   while (pos + 8 <= buf.byteLength) {
      const chunkId = String.fromCharCode(bytes[pos], bytes[pos+1], bytes[pos+2], bytes[pos+3]);
      const chunkSize = view.getUint32(pos + 4, true);
      if (chunkId === 'fmt ') {
         const audioFormat = view.getUint16(pos + 8, true);
         const channels    = view.getUint16(pos + 10, true);
         const sampleRate  = view.getUint32(pos + 12, true);
         const bitDepth    = view.getUint16(pos + 22, true);
         fmt = { audioFormat, channels, sampleRate, bitDepth };
      } else if (chunkId === 'data') {
         dataOff = pos + 8;
         dataLen = chunkSize;
      }
      pos += 8 + chunkSize + (chunkSize & 1); // pad to even
   }

   if (!fmt) throw new Error('WAV missing fmt chunk');
   if (!dataOff) throw new Error('WAV missing data chunk');

   const { channels, sampleRate, bitDepth, audioFormat } = fmt;
   const numSamples = Math.floor(dataLen / (channels * (bitDepth / 8)));
   const pcm = new Int16Array(numSamples * channels);

   if (bitDepth === 16) {
      for (let i = 0; i < numSamples * channels; i++) {
         pcm[i] = view.getInt16(dataOff + i * 2, true); // WAV is LE
      }
   } else if (bitDepth === 24) {
      for (let i = 0; i < numSamples * channels; i++) {
         const b0 = bytes[dataOff + i * 3];
         const b1 = bytes[dataOff + i * 3 + 1];
         const b2 = bytes[dataOff + i * 3 + 2];
         let val = (b2 << 16) | (b1 << 8) | b0;
         if (val & 0x800000) val |= 0xFF000000;
         pcm[i] = val >> 8; // down-scale to 16-bit
      }
   } else if (bitDepth === 8) {
      for (let i = 0; i < numSamples * channels; i++) {
         pcm[i] = (bytes[dataOff + i] - 128) << 8;
      }
   } else if (bitDepth === 32 && audioFormat === 3) {
      // 32-bit float
      for (let i = 0; i < numSamples * channels; i++) {
         const f = view.getFloat32(dataOff + i * 4, true);
         let v = Math.round(f * 32767);
         if (v > 32767) v = 32767; if (v < -32767) v = -32767;
         pcm[i] = v;
      }
   } else {
      throw new Error(`Unsupported WAV bit depth: ${bitDepth} format: ${audioFormat}`);
   }

   return { pcm, channels, sampleRate, bitDepth: 16, numSamples };
}

// Linear resampling to target sample rate (returns Int16Array, mono)
export function resample(pcm, srcRate, dstRate) {
   if (srcRate === dstRate) return pcm;
   if (!srcRate || !isFinite(srcRate) || srcRate <= 0) return pcm;
   if (!dstRate || !isFinite(dstRate) || dstRate <= 0) return pcm;
   const ratio = dstRate / srcRate;
   if (!isFinite(ratio) || ratio <= 0) return pcm;
   const outLen = Math.floor(pcm.length * ratio);
   if (outLen <= 0 || !isFinite(outLen)) return pcm;
   const out = new Int16Array(outLen);
   for (let i = 0; i < outLen; i++) {
      const srcPos = i / ratio;
      const i0 = Math.floor(srcPos);
      const i1 = Math.min(i0 + 1, pcm.length - 1);
      const frac = srcPos - i0;
      out[i] = Math.round(pcm[i0] * (1 - frac) + pcm[i1] * frac);
   }
   return out;
}

// ---- ESX exporter ----------------------------------------------------------
// samples: [{ name, pcm (Int16Array), channels (1|2), sampleRate }]
// Returns Uint8Array containing a complete .esx file
export function buildESX(samples) {
   const monoSamples = samples.filter(s => s.channels === 1);
   const stereoSamples = samples.filter(s => s.channels === 2);

   // ESX practical max file size (32MB SmartMedia)
   const ESX_MAX_FILE_SIZE = 32 * 1024 * 1024;

   // Calculate sample data size
   let dataOffset = 0;
   const sampleLayout = []; // { headerOff, dataOff, dataLen, sample }
   let overflow = false;

   for (const s of monoSamples) {
      if (sampleLayout.length >= ESX_MAX_MONO) { overflow = true; break; }
      const pcmBytes = s.pcm.length * 2;
      const totalBlock = ESX_SAMPLE_BLOCK_HEADER + pcmBytes;
      const aligned = Math.ceil(totalBlock / 4) * 4;
      if (ESX_SAMPLE_DATA_OFF + dataOffset + aligned > ESX_MAX_FILE_SIZE) { overflow = true; break; }
      sampleLayout.push({
         type: 'mono',
         index: sampleLayout.length,
         name: s.name,
         pcm: s.pcm,
         sampleRate: s.sampleRate || 32000,
         offsetStart: dataOffset,
         offsetEnd: dataOffset + aligned,  // aligned end (matches genuine Korg files)
         blockLen: aligned,
      });
      dataOffset += aligned;
   }

   const monoCount = sampleLayout.length;

   for (const s of stereoSamples) {
      if (sampleLayout.length >= ESX_MAX_MONO + ESX_MAX_STEREO) { overflow = true; break; }
      // Deinterleave: pcm is L,R,L,R...
      const numFrames = s.pcm.length / 2;
      const leftPcm = new Int16Array(numFrames);
      const rightPcm = new Int16Array(numFrames);
      for (let i = 0; i < numFrames; i++) {
         leftPcm[i] = s.pcm[i * 2];
         rightPcm[i] = s.pcm[i * 2 + 1];
      }
      const lBytes = leftPcm.length * 2;
      const rBytes = rightPcm.length * 2;
      const lBlock = ESX_SAMPLE_BLOCK_HEADER + lBytes;
      const rBlock = ESX_SAMPLE_BLOCK_HEADER + rBytes;
      const lAligned = Math.ceil(lBlock / 4) * 4;
      const rAligned = Math.ceil(rBlock / 4) * 4;
      if (ESX_SAMPLE_DATA_OFF + dataOffset + lAligned + rAligned > ESX_MAX_FILE_SIZE) { overflow = true; break; }

      sampleLayout.push({
         type: 'stereo',
         index: sampleLayout.length - monoCount,
         name: s.name,
         leftPcm, rightPcm,
         sampleRate: s.sampleRate || 32000,
         numFrames,
         offsetLStart: dataOffset,
         offsetLEnd: dataOffset + lAligned,      // aligned end
         offsetRStart: dataOffset + lAligned,
         offsetREnd: dataOffset + lAligned + rAligned,  // aligned end
         blockLen: lAligned + rAligned,
      });
      dataOffset += lAligned + rAligned;
   }

   const stereoCount = sampleLayout.length - monoCount;
   const totalFileSize = ESX_SAMPLE_DATA_OFF + dataOffset;
   const buffer = new ArrayBuffer(totalFileSize);
   const view = new DataView(buffer);
   const bytes = new Uint8Array(buffer);

   // -- File header (32 bytes at 0x00000000) --
   writeAscii(view, 0x00, 'KORG', 4);
   view.setUint32(0x04, 0x00000071, false);
   writeAscii(view, 0x08, 'ESX', 3); // 3 bytes only; byte 0x0B must be 0x00 (null-terminated) so parsers detect 'ESX' not 'ESX '
   // 0x0C-0x0F: checksum (use zeros, hardware may not validate)
   view.setUint16(0x10, 0x0001, false); // version major (genuine files: 0x0001)
   view.setUint16(0x12, 0x0000, false); // version minor

   // -- Global parameters (192 bytes at 0x20) --
   // Copy minimal defaults from genuine .esx files
   view.setUint32(0x20, 0x00000001, false);
   view.setUint16(0x24, 0x000F, false);
   view.setUint16(0x26, 0x0E00, false);
   bytes[0x28] = 0x01; bytes[0x29] = 0x09;
   bytes[0x2A] = 0x24; bytes[0x2B] = 0x26; bytes[0x2C] = 0x28; bytes[0x2D] = 0x29;
   bytes[0x2E] = 0x2B; bytes[0x2F] = 0x2A;

   // -- Pattern section (256 patterns × 4280 bytes at 0x200) --
   // Initialize all pattern names to spaces (0x20)
   for (let i = 0; i < 256; i++) {
      const off = 0x200 + i * 4280;
      for (let j = 0; j < 8; j++) bytes[off + j] = 0x20;
   }

   // -- Song section (64 songs × 528 bytes at 0x130000) --
   for (let i = 0; i < 64; i++) {
      const off = 0x130000 + i * 528;
      for (let j = 0; j < 8; j++) bytes[off + j] = 0x20;
   }

   // -- Sample section header (32 bytes at 0x1B0000) --
   writeAscii(view, ESX_SAMPLE_SECTION, 'KORG', 4);
   view.setUint32(ESX_SAMPLE_SECTION + 0x04, 0x00000071, false);
   writeAscii(view, ESX_SAMPLE_SECTION + 0x08, 'BPS', 4); // genuine files have 'BPS ' (space at 0x0B)
   // 0x0C-0x0F: checksum (zeros)
   // Version string "01000000" (matching genuine Korg files)
   writeAscii(view, ESX_SAMPLE_SECTION + 0x10, '01000000', 8);

   // -- Sample counts --
   writeUint32BE(view, ESX_NUM_MONO_OFF, monoCount);
   writeUint32BE(view, ESX_NUM_STEREO_OFF, stereoCount);
   writeUint32BE(view, ESX_CUR_OFFSET_OFF, dataOffset);

   // -- Mono sample headers (256 × 40 bytes at 0x1B0100) --
   for (let i = 0; i < ESX_MAX_MONO; i++) {
      const off = ESX_MONO_HEADER_OFF + i * ESX_MONO_HEADER_SIZE;
      const layout = sampleLayout[i];
      if (layout && layout.type === 'mono') {
         writeAscii(view, off, layout.name.slice(0, 8), 8, 0x00);  // null-padded (matching genuine Korg files)
         writeUint32BE(view, off + 0x08, layout.offsetStart);
         writeUint32BE(view, off + 0x0C, layout.offsetEnd);
         writeUint32BE(view, off + 0x10, 0);           // start
         writeUint32BE(view, off + 0x14, layout.pcm.length); // end = num samples
         writeUint32BE(view, off + 0x18, layout.pcm.length); // loop start = end (no loop)
         writeUint32BE(view, off + 0x1C, layout.sampleRate);
         writeInt16BE(view, off + 0x20, 0);            // tune
         bytes[off + 0x22] = 0x00;                     // level (genuine files use 0)
         bytes[off + 0x23] = 0x00;                     // reserved
         bytes[off + 0x24] = 0x80;                     // stretch step (genuine files use 0x80)
      } else {
         // Empty slot pattern (matching genuine Korg files):
         // name=spaces, offsetStart=0xFFFFFFFF, rest=0x00 except sampleRate=44100, byte 0x23=0x80
         writeAscii(view, off, '        ', 8);
         writeUint32BE(view, off + 0x08, 0xFFFFFFFF);  // offsetStart = -1 (empty marker)
         writeUint32BE(view, off + 0x0C, 0);            // offsetEnd
         writeUint32BE(view, off + 0x10, 0);            // start
         writeUint32BE(view, off + 0x14, 0);            // end
         writeUint32BE(view, off + 0x18, 0);            // loopStart
         writeUint32BE(view, off + 0x1C, 44100);        // sampleRate (default)
         writeInt16BE(view, off + 0x20, 0);             // tune
         bytes[off + 0x22] = 0x00;                      // level
         bytes[off + 0x23] = 0x00;                      // reserved
         bytes[off + 0x24] = 0x80;                      // stretch step
      }
   }

   // -- Stereo sample headers (128 × 44 bytes at 0x1B2900) --
   for (let i = 0; i < ESX_MAX_STEREO; i++) {
      const off = ESX_STEREO_HEADER_OFF + i * ESX_STEREO_HEADER_SIZE;
      const layout = sampleLayout[monoCount + i];
      if (layout && layout.type === 'stereo') {
         writeAscii(view, off, layout.name.slice(0, 8), 8, 0x00);  // null-padded
         writeUint32BE(view, off + 0x08, layout.offsetLStart);
         writeUint32BE(view, off + 0x0C, layout.offsetLEnd);
         writeUint32BE(view, off + 0x10, layout.offsetRStart);
         writeUint32BE(view, off + 0x14, layout.offsetREnd);
         writeUint32BE(view, off + 0x18, 0);              // start
         writeUint32BE(view, off + 0x1C, layout.numFrames); // end
         writeUint32BE(view, off + 0x20, layout.sampleRate);
         writeInt16BE(view, off + 0x24, 0);               // tune
         bytes[off + 0x26] = 0x00;                        // level (genuine files use 0)
         bytes[off + 0x27] = 0x00;                        // reserved
         bytes[off + 0x28] = 0x80;                        // stretch step
      } else {
         // Empty stereo slot: all 0xFF (matching genuine Korg files)
         for (let j = 0; j < ESX_STEREO_HEADER_SIZE; j++) bytes[off + j] = 0xFF;
      }
   }

   // -- Sample data (at 0x250000) --
   for (const layout of sampleLayout) {
      if (layout.type === 'mono') {
         const absOff = ESX_SAMPLE_DATA_OFF + layout.offsetStart;
         // 16-byte sample data block header
         writeUint32BE(view, absOff, 0x80007FFF);
         writeUint32BE(view, absOff + 0x04, layout.offsetStart);
         writeUint32BE(view, absOff + 0x08, layout.offsetEnd);
         bytes[absOff + 0x0C] = sampleLayout.indexOf(layout) & 0xFF; // sample number
         bytes[absOff + 0x0D] = 0x00; // mono
         bytes[absOff + 0x0E] = 0xFF;
         bytes[absOff + 0x0F] = 0xFF;
         // Write PCM (big-endian)
         for (let i = 0; i < layout.pcm.length; i++) {
            view.setInt16(absOff + 0x10 + i * 2, layout.pcm[i], false);
         }
      } else if (layout.type === 'stereo') {
         // Left channel
         const lAbs = ESX_SAMPLE_DATA_OFF + layout.offsetLStart;
         writeUint32BE(view, lAbs, 0x80007FFF);
         writeUint32BE(view, lAbs + 0x04, layout.offsetLStart);
         writeUint32BE(view, lAbs + 0x08, layout.offsetLEnd);
         bytes[lAbs + 0x0C] = (monoCount + layout.index) & 0xFF;
         bytes[lAbs + 0x0D] = 0x01; // left
         bytes[lAbs + 0x0E] = 0xFF;
         bytes[lAbs + 0x0F] = 0xFF;
         for (let i = 0; i < layout.leftPcm.length; i++) {
            view.setInt16(lAbs + 0x10 + i * 2, layout.leftPcm[i], false);
         }
         // Right channel
         const rAbs = ESX_SAMPLE_DATA_OFF + layout.offsetRStart;
         writeUint32BE(view, rAbs, 0x80007FFF);
         writeUint32BE(view, rAbs + 0x04, layout.offsetRStart);
         writeUint32BE(view, rAbs + 0x08, layout.offsetREnd);
         bytes[rAbs + 0x0C] = (monoCount + layout.index) & 0xFF;
         bytes[rAbs + 0x0D] = 0x02; // right
         bytes[rAbs + 0x0E] = 0xFF;
         bytes[rAbs + 0x0F] = 0xFF;
         for (let i = 0; i < layout.rightPcm.length; i++) {
            view.setInt16(rAbs + 0x10 + i * 2, layout.rightPcm[i], false);
         }
      }
   }

   bytes._overflow = overflow;
   bytes._written = sampleLayout.length;
   bytes._total = monoSamples.length + stereoSamples.length;
   return bytes;
}

// ---- ES1 ADPCM compression -------------------------------------------------
// Analytical encoder ported from Korg's es2wav.exe via polluxsynth/es12wav.
// Analyzes each 32-sample frame to pick optimal tableno, bitdynamics, stepsize,
// and maxdiff parameters, then encodes deltas directly (no brute-force search).

function stepsizeToIndex(s, tn) {
   const t = ES1_STEPSIZE_TABLE[tn];
   let tp = 47, idx = 0;
   tp -= 16;
   if (s >= t[tp + 16]) { idx += 48; tp += 24; }
   else {
      tp -= 16;
      if (s >= t[tp + 16]) { idx += 32; tp += 24; }
      else { tp -= 8; if (s >= t[tp + 8]) { idx += 16; tp += 16; } }
   }
   tp -= 4; if (s >= t[tp + 4]) { idx += 8; tp += 8; }
   tp -= 2; if (s >= t[tp + 2]) { idx += 4; tp += 4; }
   tp -= 1; if (s >= t[tp + 1]) { idx += 2; tp += 2; }
   if (s >= t[tp]) idx += 1;
   return Math.min(63, Math.max(0, idx));
}

function es1EncodeFrame(samples) {
   const fsv = samples[0];
   let dMax = 0, sdMax = 0, dSum = 0;
   const sd = Math.abs(samples[1] - samples[0]);
   for (let i = 1; i < ES1_FRAME_SIZE; i++) {
      const d = Math.abs(samples[i] - samples[i - 1]);
      const s = Math.abs(samples[i] - fsv);
      if (d > dMax) dMax = d;
      if (s > sdMax) sdMax = s;
      dSum += d;
   }
   const dAvg = dSum >> 5;
   const tnT = (dMax * 73 + Math.floor((dMax * 9362) / 65536)) >> 7;
   let tn = 3;
   if (tnT <= ES1_STEPSIZE_TABLE[0][63]) tn = 0;
   else if (tnT <= ES1_STEPSIZE_TABLE[1][63]) tn = 1;
   else if (tnT <= ES1_STEPSIZE_TABLE[2][63]) tn = 2;

   let bd = 0, sdm = sdMax;
   if (sdm > 255) { bd += 8; sdm >>= 8; }
   if (sdm > 15)  { bd += 4; sdm >>= 4; }
   if (sdm > 3)   { bd += 2; sdm >>= 2; }
   if (sdm > 1)   { bd += 1; }
   bd = Math.min(15, bd);

   const mi = stepsizeToIndex((dMax * 63 + Math.floor((dMax * 32509) / 65536)) >> 7, tn);
   let sd2 = sd; if (sd2 <= dAvg << 1) sd2 = dAvg;
   const si = Math.max(stepsizeToIndex(sd2, tn), mi);

   const T = ES1_STEPSIZE_TABLE[tn];
   const dyn = (1 << (bd + 1)) - 1;
   const hv = Math.min(65535, fsv + dyn);
   const lv = Math.max(-65535, fsv - dyn);
   let ssp = si, cv = fsv;
   const dl = new Uint8Array(ES1_FRAME_SIZE - 1);

   for (let i = 1; i < ES1_FRAME_SIZE; i++) {
      let nv = fsv + cv; nv += (nv < 0 ? 1 : 0); nv >>= 1;
      const tmp = T[ssp] << 1, mnv = hv - tmp, mxv = lv + tmp;
      let vc;
      if (cv >= mnv) {
         if (cv <= mxv) { cv = nv; vc = 3; ssp = Math.max(0, ssp - 1); }
         else { cv = mnv; vc = 2; }
      } else {
         if (cv <= mxv) { cv = mxv; vc = 1; }
         else { vc = 0; }
      }
      let diff = samples[i] - cv;
      const sgn = diff < 0 ? 64 : 0;
      if (sgn) diff = -diff;
      const delta = Math.min(63, Math.floor((diff * 32) / T[ssp]));
      const vp = ((2 * delta + 1) * T[ssp]) >> 6;
      cv = sgn ? cv - vp : cv + vp;
      cv = Math.max(-32767, Math.min(32767, cv));
      dl[i - 1] = delta | sgn;
      if (vc > 1) ssp = (vc === 2 && sgn) ? ssp + ES1_INDEX_TABLE[delta] : ssp - 1;
      else        ssp = (vc === 1 && sgn) ? ssp - 1 : ssp + ES1_INDEX_TABLE[delta];
      ssp = Math.max(mi, Math.min(63, ssp));
   }

   const o = new Uint8Array(ES1_FRAME_SIZE);
   const u = fsv & 0xFFFF;
   o[0] = (u >> 8) & 0xFF; o[1] = u & 0xFF;
   o[2] = ((si & 0x3F) << 2) | ((mi >> 4) & 0x03);
   o[3] = ((mi & 0x0F) << 4) | (bd & 0x0F);
   o[4] = ((tn & 0x03) << 6) | ((dl[0] >> 6) & 0x01);
   o[ 5] = ((dl[ 0] & 63) << 2) | (dl[ 1] >> 5);
   o[ 6] = ((dl[ 1] & 31) << 3) | (dl[ 2] >> 4);
   o[ 7] = ((dl[ 2] & 15) << 4) | (dl[ 3] >> 3);
   o[ 8] = ((dl[ 3] &  7) << 5) | (dl[ 4] >> 2);
   o[ 9] = ((dl[ 4] &  3) << 6) | (dl[ 5] >> 1);
   o[10] = ((dl[ 5] &  1) << 7) |  dl[ 6];
   o[11] = ( dl[ 7]      << 1) | (dl[ 8] >> 6);
   o[12] = ((dl[ 8] & 63) << 2) | (dl[ 9] >> 5);
   o[13] = ((dl[ 9] & 31) << 3) | (dl[10] >> 4);
   o[14] = ((dl[10] & 15) << 4) | (dl[11] >> 3);
   o[15] = ((dl[11] &  7) << 5) | (dl[12] >> 2);
   o[16] = ((dl[12] &  3) << 6) | (dl[13] >> 1);
   o[17] = ((dl[13] &  1) << 7) |  dl[14];
   o[18] = ( dl[15]      << 1) | (dl[16] >> 6);
   o[19] = ((dl[16] & 63) << 2) | (dl[17] >> 5);
   o[20] = ((dl[17] & 31) << 3) | (dl[18] >> 4);
   o[21] = ((dl[18] & 15) << 4) | (dl[19] >> 3);
   o[22] = ((dl[19] &  7) << 5) | (dl[20] >> 2);
   o[23] = ((dl[20] &  3) << 6) | (dl[21] >> 1);
   o[24] = ((dl[21] &  1) << 7) |  dl[22];
   o[25] = ( dl[23]      << 1) | (dl[24] >> 6);
   o[26] = ((dl[24] & 63) << 2) | (dl[25] >> 5);
   o[27] = ((dl[25] & 31) << 3) | (dl[26] >> 4);
   o[28] = ((dl[26] & 15) << 4) | (dl[27] >> 3);
   o[29] = ((dl[27] &  7) << 5) | (dl[28] >> 2);
   o[30] = ((dl[28] &  3) << 6) | (dl[29] >> 1);
   o[31] = ((dl[29] &  1) << 7) |  dl[30];
   return o;
}

// Compress a full PCM channel into ADPCM frames
function es1CompressChannel(pcm) {
   const numFrames = Math.ceil(pcm.length / ES1_FRAME_SIZE);
   const paddedLen = numFrames * ES1_FRAME_SIZE;
   const out = new Uint8Array(paddedLen);
   const buf = new Int16Array(ES1_FRAME_SIZE);

   for (let fn = 0; fn < numFrames; fn++) {
      for (let i = 0; i < ES1_FRAME_SIZE; i++) {
         buf[i] = (fn * ES1_FRAME_SIZE + i < pcm.length) ? pcm[fn * ES1_FRAME_SIZE + i] : 0;
      }
      out.set(es1EncodeFrame(buf), fn * ES1_FRAME_SIZE);
   }

   return out;
}

// ---- ES1 exporter ----------------------------------------------------------
// samples: [{ name, pcm (Int16Array), channels (1|2), sampleRate }]
// Returns Uint8Array containing a complete .es1 file (3,801,088 bytes)
export function buildES1(samples, onProgress) {
   // ES1 is mono-only at 32 kHz. Downmix any stereo samples to mono.
   const monoSamples = samples.map(s => {
      if (s.channels === 2) {
         const numFrames = s.pcm.length / 2;
         const mono = new Int16Array(numFrames);
         for (let j = 0; j < numFrames; j++) {
            mono[j] = Math.round((s.pcm[j * 2] + s.pcm[j * 2 + 1]) / 2);
         }
         return { ...s, pcm: mono, channels: 1 };
      }
      return s;
   }).slice(0, ES1_NUM_MONO);

   // Full-size file buffer, zero-filled. Audio zone will be filled with 0xFF.
   const buffer = new ArrayBuffer(ES1_FILE_SIZE);
   const view = new DataView(buffer);
   const bytes = new Uint8Array(buffer);

   // -- Fill audio zone with 0xFF (erased NOR flash) --
   bytes.fill(0xFF, ES1_SAMPLE_DATA_OFF, ES1_AUDIO_ZONE_END);

   // -- Headers --
   // HDR1 at 0x000000
   const HDR1 = [0x4B, 0x4F, 0x52, 0x47, 0x01, 0x00, 0x57, 0x02, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0xBB, 0xB3];
   HDR1.forEach((b, i) => { bytes[i] = b; });
   // HDR2 at 0x080000
   const HDR2 = [0x4B, 0x4F, 0x52, 0x47, 0x01, 0x00, 0x57, 0x01, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0xAF, 0x3E];
   HDR2.forEach((b, i) => { bytes[ES1_HEADER_POS + i] = b; });

   // -- Initialize all slot records to empty --
   for (let i = 0; i < ES1_NUM_MONO; i++) {
      const off = ES1_MONO_HEADER_OFF + i * ES1_MONO_HEADER_SIZE;
      for (let b = 0; b < ES1_MONO_HEADER_SIZE; b++) bytes[off + b] = 0;
      bytes[off + 21] = 0xFF; // empty status
   }
   for (let i = 0; i < ES1_NUM_STEREO; i++) {
      const off = ES1_STEREO_HEADER_OFF + i * ES1_STEREO_HEADER_SIZE;
      for (let b = 0; b < ES1_STEREO_HEADER_SIZE; b++) bytes[off + b] = 0;
      bytes[off + 21] = 0xFF; // empty status
   }

   // -- Compress and write mono samples --
   let dataCursor = 0; // offset from ES1_SAMPLE_DATA_OFF
   let maxEndAddrB = 0;
   let written = 0;
   let overflow = false;

   for (let i = 0; i < monoSamples.length; i++) {
      const s = monoSamples[i];
      if (onProgress) onProgress(`Compressing mono ${i + 1}/${monoSamples.length}…`);

      // Resample to 32kHz
      const resampled = resample(s.pcm, s.sampleRate || 44100, ES1_SAMPLE_RATE);
      const compressed = es1CompressChannel(resampled);

      // Address calculations:
      //   Range A (sample start/end) = file_offset + 0x160000
      //   Range B (data start/end)   = file_offset + 0x060000
      const fileOff = ES1_SAMPLE_DATA_OFF + dataCursor;
      const staddrA = fileOff + ES1_RANGE_A_OFFSET;
      const endaddrA = staddrA + resampled.length - 1;
      const staddrB = fileOff + ES1_RANGE_B_OFFSET;
      const endaddrB = staddrB + compressed.length - 1;

      if (fileOff + compressed.length > ES1_AUDIO_ZONE_END) { overflow = true; break; }

      // Write ADPCM data
      bytes.set(compressed, fileOff);

      // Write slot record (26 bytes)
      const off = ES1_MONO_HEADER_OFF + i * ES1_MONO_HEADER_SIZE;
      writeUint24BE(bytes, off + 0, staddrA);    // [0-2] STADDR_A
      writeUint24BE(bytes, off + 3, endaddrA);   // [3-5] ENDADDR_A
      writeUint24BE(bytes, off + 6, staddrB);    // [6-8] STADDR_B
      writeUint24BE(bytes, off + 9, endaddrB);   // [9-11] ENDADDR_B
      bytes[off + 21] = i & 0xFF;                // [21] STATUS = slot number

      if (endaddrB > maxEndAddrB) maxEndAddrB = endaddrB;
      dataCursor += compressed.length;
      written++;
   }

   // Attach overflow info as properties on the returned Uint8Array
   bytes._overflow = overflow;
   bytes._written = written;
   bytes._total = monoSamples.length;
   return bytes;
}
